import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Frame variants. `default` is the card: a muted shell around a white,
 * bordered panel. `flat` is the labelled section used inside dialogs
 * (phase details): no shell, a small title, and a borderless muted panel.
 * Slots read the variant from context, so a call site sets it once on
 * <Frame> and the header, title and panel follow; a Frame without `variant`
 * inherits the enclosing one. Dialogs, sheets and drawers are flat already
 * (FrameVariantScope), so Frames there need no `variant`.
 *
 * Insets belong to the variant: the header, the footer and a card table's
 * outer cells share one left edge, and a panel pads its content. Call sites
 * set layout (flex, gap), not insets — at most `p-0` on a panel whose content
 * pads itself, or `p-3` for a compact one — so switching DEFAULT_FRAME_VARIANT
 * restyles every section without leaving one misaligned.
 *
 * A header's button, menu, switch or badge goes in <FrameAction> inside the
 * FrameHeader — it lands top-right; no hand-made flex rows or absolute buttons.
 *
 * Tables go straight on the shell as <Table variant="card">, not inside a
 * FramePanel: the column headers sit on the shell under the FrameHeader, a
 * rule separates the two (Clerk-style), and the rows become the panel (see
 * table.tsx).
 *
 * A RadioGroup of RadioCards also goes straight on the shell: its cards are
 * panels side by side — 4px apart, 4px from the panels around them, with the
 * panel border (see RadioCard).
 */

/** The variant every <Frame> without an explicit `variant` gets — change it here, nowhere else. */
const DEFAULT_FRAME_VARIANT = "default" as const;

export const frameVariants = cva(
  "relative flex flex-col rounded-xl *:[[data-slot=frame-panel]+[data-slot=frame-panel]]:mt-1 *:[[data-slot=frame-panel]+[data-slot=radio-group]]:mt-1 *:[[data-slot=radio-group]+[data-slot=frame-panel]]:mt-1 *:data-[slot=radio-group]:gap-1",
  {
    variants: {
      variant: {
        default: "bg-muted/70 dark:bg-sidebar p-1",
        flat: "bg-transparent p-0",
      },
    },
    defaultVariants: { variant: DEFAULT_FRAME_VARIANT },
  },
);

export const framePanelVariants = cva(
  "relative rounded-lg bg-clip-padding p-4 before:pointer-events-none before:absolute before:rounded-[calc(var(--radius-lg)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] dark:before:shadow-[0_-1px_--theme(--color-white/6%)]",
  {
    variants: {
      variant: {
        default: "border bg-background shadow-xs/5 radius-full:shadow-soft",
        flat: "overflow-hidden bg-muted/70 dark:bg-sidebar in-dialog:bg-sidebar dark:in-dialog:bg-background",
      },
    },
    defaultVariants: { variant: DEFAULT_FRAME_VARIANT },
  },
);

// Directly above a card table the header draws the rule under itself, across
// the whole frame: the default variant bleeds out of the shell's p-1 and adds
// it back to the padding, so the text stays put.
// With a FrameAction inside, the header is a grid: title and description in
// the first column, the action top-right (centred on a title-only header).
const frameHeaderVariants = cva(
  "flex flex-col has-data-[slot=frame-action]:grid has-data-[slot=frame-action]:grid-cols-[minmax(0,1fr)_auto] has-data-[slot=frame-action]:gap-x-4",
  {
    variants: {
      variant: {
        default:
          "px-3.5 py-2.5 [&:has(+[data-slot=table-container][data-variant=card])]:-mx-1 [&:has(+[data-slot=table-container][data-variant=card])]:px-4.5 [&:has(+[data-slot=table-container][data-variant=card])]:border-b",
        flat: "p-1 [&:has(+[data-slot=table-container][data-variant=card])]:border-b",
      },
    },
    defaultVariants: { variant: DEFAULT_FRAME_VARIANT },
  },
);

export const frameFooterVariants = cva("pt-2 pb-1.5 text-xs text-muted-foreground", {
  variants: {
    variant: {
      default: "px-1.5",
      flat: "px-1",
    },
  },
  defaultVariants: { variant: DEFAULT_FRAME_VARIANT },
});

const frameTitleVariants = cva("font-medium", {
  variants: {
    variant: {
      default: "text-base",
      flat: "text-sm",
    },
  },
  defaultVariants: { variant: DEFAULT_FRAME_VARIANT },
});

export type FrameVariant = NonNullable<VariantProps<typeof frameVariants>["variant"]>;

const FrameVariantContext = React.createContext<FrameVariant>(DEFAULT_FRAME_VARIANT);

/** The enclosing Frame's (or FrameVariantScope's) variant — DEFAULT_FRAME_VARIANT outside both. */
export function useFrameVariant(): FrameVariant {
  return React.useContext(FrameVariantContext);
}

/**
 * Sets the variant every Frame below defaults to (a Frame without `variant`
 * inherits the nearest one). Dialogs, sheets and drawers wrap their content in
 * a flat scope: a section there is a label over a muted box, never a box in a
 * box — and since context crosses portals, a dialog opened from inside a page
 * Frame would otherwise inherit that Frame's variant.
 */
export function FrameVariantScope({
  variant,
  children,
}: {
  variant: FrameVariant;
  children: React.ReactNode;
}): React.ReactElement {
  return <FrameVariantContext.Provider value={variant}>{children}</FrameVariantContext.Provider>;
}

/**
 * @example
 * import { Button } from "@/components/ui/button"
 * import { Frame, FrameAction, FrameDescription, FrameHeader, FramePanel, FrameTitle } from "@/components/ui/frame"
 * import { Input } from "@/components/ui/input"
 * import { Label } from "@/components/ui/label"
 *
 * export function SenderSection() {
 *   return (
 *     <Frame>
 *       <FrameHeader>
 *         <FrameTitle>Sender</FrameTitle>
 *         <FrameDescription>The name creators see in their inbox.</FrameDescription>
 *         <FrameAction>
 *           <Button size="sm" variant="secondary">Reset</Button>
 *         </FrameAction>
 *       </FrameHeader>
 *       <FramePanel className="flex flex-col gap-2">
 *         <Label htmlFor="sender-name">Display name</Label>
 *         <Input id="sender-name" defaultValue="Amy from Uplodio" />
 *       </FramePanel>
 *     </Frame>
 *   )
 * }
 */
export function Frame({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof frameVariants>): React.ReactElement {
  const inherited = React.useContext(FrameVariantContext);
  const resolved: FrameVariant = variant ?? inherited;
  return (
    <FrameVariantContext.Provider value={resolved}>
      <div
        className={cn(frameVariants({ variant: resolved }), className)}
        data-slot="frame"
        data-variant={resolved}
        {...props}
      />
    </FrameVariantContext.Provider>
  );
}

export function FramePanel({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  const variant = React.useContext(FrameVariantContext);
  return (
    <div
      className={cn(framePanelVariants({ variant }), className)}
      data-slot="frame-panel"
      {...props}
    />
  );
}

export function FrameHeader({
  className,
  ...props
}: React.ComponentProps<"header">): React.ReactElement {
  const variant = React.useContext(FrameVariantContext);
  return (
    <header
      className={cn(frameHeaderVariants({ variant }), className)}
      data-slot="frame-header"
      {...props}
    />
  );
}

export function FrameTitle({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  const variant = React.useContext(FrameVariantContext);
  return (
    <div
      className={cn(frameTitleVariants({ variant }), className)}
      data-slot="frame-title"
      {...props}
    />
  );
}

/** A count beside a FrameTitle — "(8)", mono and muted. Pass the number. */
export function FrameCount({
  className,
  children,
  ...props
}: React.ComponentProps<"span">): React.ReactElement {
  return (
    <span
      className={cn("ms-1 font-mono text-sm font-normal text-muted-foreground", className)}
      data-slot="frame-count"
      {...props}
    >
      ({children})
    </span>
  );
}

export function FrameDescription({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      className={cn("text-muted-foreground text-xs", className)}
      data-slot="frame-description"
      {...props}
    />
  );
}

/** A header's action (button, menu, switch, badge): top-right of the FrameHeader. */
export function FrameAction({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      className={cn(
        "col-start-2 row-span-2 row-start-1 flex items-center gap-2 self-start justify-self-end [[data-slot=frame-header]:not(:has([data-slot=frame-description]))>&]:self-center",
        className,
      )}
      data-slot="frame-action"
      {...props}
    />
  );
}

export function FrameFooter({
  className,
  ...props
}: React.ComponentProps<"footer">): React.ReactElement {
  const variant = React.useContext(FrameVariantContext);
  return (
    <footer
      className={cn(frameFooterVariants({ variant }), className)}
      data-slot="frame-footer"
      {...props}
    />
  );
}
